import React, {useCallback, useEffect, useMemo, useRef} from 'react';

import type {IconProp} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronLeft, faChevronRight} from '@fortawesome/free-solid-svg-icons';

import type {DocumentUploadFile} from './documentDefinitions';

const chevronLeftIcon = faChevronLeft as IconProp;
const chevronRightIcon = faChevronRight as IconProp;

type DocumentPreviewModalProps = {
  open: boolean;
  file: DocumentUploadFile | null;
  title: string;
  files: DocumentUploadFile[];
  activeFileUid: string | null;
  onSelectFile: (file: DocumentUploadFile) => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  onClose: () => void;
  onDownload: (file: DocumentUploadFile) => void;
  onRemove: (file: DocumentUploadFile) => void;
  removing?: boolean;
};

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif']);

function getExtension(file?: DocumentUploadFile | null) {
  return String(file?.extension || file?.name?.split('.').pop() || '')
    .trim()
    .toLowerCase();
}

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  open,
  file,
  title,
  files,
  activeFileUid,
  onSelectFile,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  onClose,
  onDownload,
  onRemove,
  removing = false,
}) => {
  const stripRef = useRef<HTMLDivElement | null>(null);

  const fileIndex = useMemo(
    () => files.findIndex((item) => item.uid === activeFileUid),
    [activeFileUid, files],
  );

  const canCycle = files.length > 1;

  const handleStripWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      const strip = stripRef.current;
      if (!strip) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      strip.scrollBy({left: event.deltaY});
    },
    [],
  );

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === 'ArrowLeft' && hasPrevious) {
        event.preventDefault();
        onPrevious();
      }

      if (event.key === 'ArrowRight' && hasNext) {
        event.preventDefault();
        onNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasNext, hasPrevious, onClose, onNext, onPrevious, open]);

  useEffect(() => {
    if (!open || !activeFileUid || !stripRef.current) return;

    const activeThumb = stripRef.current.querySelector<HTMLButtonElement>(
      `[data-file-uid="${activeFileUid}"]`,
    );

    activeThumb?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: 'smooth',
    });
  }, [activeFileUid, open]);

  if (!open || !file) return null;

  const extension = getExtension(file);
  const isImage = IMAGE_EXTENSIONS.has(extension);
  const isPdf = extension === 'pdf';

  return (
    <div
      className="company-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${file.name}`}
    >
      <div className="company-modal__backdrop" onClick={onClose} />

      <div
        className={`company-modal__panel document-preview-modal${
          isPdf ? ' document-preview-modal--pdf' : ''
        }`}
      >
        <header>
          <h3>{title}</h3>
          <button
            type="button"
            className="link-button"
            onClick={onClose}
            aria-label="Close preview"
          >
            Close
          </button>
        </header>

        <p className="company-modal__description">{file.name}</p>

        {canCycle ? (
          <div className="document-preview-modal__nav">
            <button
              type="button"
              className="money-btn money-btn--secondary money-btn--sm"
              onClick={onPrevious}
              disabled={!hasPrevious || removing}
            >
              Previous
            </button>

            <span className="document-preview-modal__nav-status">
              {fileIndex >= 0 ? fileIndex + 1 : 1} of {files.length}
            </span>

            <button
              type="button"
              className="money-btn money-btn--secondary money-btn--sm"
              onClick={onNext}
              disabled={!hasNext || removing}
            >
              Next
            </button>
          </div>
        ) : null}

        {canCycle ? (
          <div
            className="document-preview-modal__strip"
            aria-label="Documents in this section"
            ref={stripRef}
            onWheel={handleStripWheel}
          >
            {files.map((item) => {
              const itemExtension = getExtension(item);
              const itemIsImage = IMAGE_EXTENSIONS.has(itemExtension);
              const extensionLabel = String(itemExtension || 'file')
                .toUpperCase()
                .slice(0, 4);
              const isActive = item.uid === activeFileUid;

              return (
                <button
                  key={item.uid}
                  type="button"
                  className={`document-preview-modal__thumb${
                    isActive ? ' is-active' : ''
                  }`}
                  data-file-uid={item.uid}
                  onClick={() => onSelectFile(item)}
                  title={item.name}
                  aria-label={`Preview ${item.name}`}
                >
                  {itemIsImage && item.url ? (
                    <img src={item.url} alt={item.name} loading="lazy" />
                  ) : (
                    <span>{extensionLabel}</span>
                  )}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="document-preview-modal__viewer">
          {canCycle ? (
            <button
              type="button"
              className="document-preview-modal__viewer-nav document-preview-modal__viewer-nav--prev"
              onClick={onPrevious}
              disabled={!hasPrevious || removing}
              aria-label="Previous document"
            >
              <FontAwesomeIcon icon={chevronLeftIcon} />
            </button>
          ) : null}

          <div className="document-preview-modal__viewer-content">
            {isImage && file.url ? (
              <img src={file.url} alt={file.name} />
            ) : isPdf && file.url ? (
              <iframe src={file.url} title={file.name} />
            ) : (
              <div className="document-preview-modal__fallback">
                <strong>Preview not available for this file type.</strong>
                <p>Use download to open this document on your device.</p>
              </div>
            )}
          </div>

          {canCycle ? (
            <button
              type="button"
              className="document-preview-modal__viewer-nav document-preview-modal__viewer-nav--next"
              onClick={onNext}
              disabled={!hasNext || removing}
              aria-label="Next document"
            >
              <FontAwesomeIcon icon={chevronRightIcon} />
            </button>
          ) : null}
        </div>

        <footer className="company-modal__actions">
          <button
            type="button"
            className="money-btn money-btn--secondary money-btn--sm"
            onClick={onClose}
            disabled={removing}
          >
            Done
          </button>

          <button
            type="button"
            className="money-btn money-btn--secondary money-btn--sm"
            onClick={() => onDownload(file)}
            disabled={!file.url || removing}
          >
            Download
          </button>

          <button
            type="button"
            className="money-btn money-btn--danger money-btn--sm"
            onClick={() => onRemove(file)}
            disabled={removing}
          >
            {removing ? 'Removing...' : 'Remove'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
